
import "./UpdateRate.css";
import Grid from "@mui/material/Grid";
import Box from '@mui/material/Box';
import { useEffect, useState } from "react";
import { AdminUserSettingsHeaderLink } from "../../components/adminSettingsHeaderLinks/AdminUserSettingsHeaderLink.jsx";
import LoadingSpinner from "../../components/loadSpinner/LoadingSpinner.jsx";
import moment from "moment";
import { getAllSystemRolesRequest } from "../../services/wisdomServices/systemService";
import { createUserRateRequest, getUserHistoricalRateRequest, getUserRateRequest, updateUserPayFrequencyRequest } from "../../services/wisdomServices/rateService";
import { updateUserSystemRole } from "../../services/wisdomServices/userService";
import { getSessionItem } from "../../utils/helper/session";
import WisdomDropdown from "../../components/dropdown/WisdomDropdown.jsx";
import { payFrequencies } from "../../utils/helper/payFrequencies";
import { Tag } from "primereact/tag";
import WisdomDataTable from "../../components/dataTable/WisdomDataTable.jsx";
import { FilterMatchMode } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { getTeamMembers } from "../../services/wisdomServices/managerService";
import { AutoComplete } from "primereact/autocomplete";
import { Can } from "../../security/Can.jsx";
import { PERMISSIONS } from "../../security/permissions.generated.js";


/**
 * @function UpdateUser
 * @returns {JSX.Element} - update rate management page
 */
function UpdateUser () {
    const user = getSessionItem("User")
    const [users, setUsers] = useState([]);
    const [searchString, setSearchString] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(true)
    let [role, setRole] = useState(2);
    const [roles, setRoles] = useState([]);
    let [data, setData] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    let [loading, setLoading] = useState(false);
    let [selectedFrequency, setSelectedFrequency] = useState('');
    const [rateInput, setRateInput] = useState();
    const [currentRate, setCurrentRate] = useState();
    const [filters, setFilters] = useState({
            global: {value:null, matchMode: FilterMatchMode.CONTAINS},
            updatedBy: {value:null, matchMode: FilterMatchMode.CONTAINS},
            fromRate: {value:null, matchMode: FilterMatchMode.CONTAINS},
            toRate: {value:null, matchMode: FilterMatchMode.CONTAINS},
    })
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const globalFilters = ['updatedBy', 'fromRate', 'toRate']
    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };
    
    const getFrequencyTagColor = (payFrequency) => {
        switch (payFrequency){
            case "monthly":
                return "success"
                
            case "weekly":
                return "info"
                    
            case "biWeekly":
                return "warning"
                        
            default:
                return "success"
        }
    }

    const getFrequencyType = (payFrequency) => {
        switch (payFrequency){
            case "monthly":
                return "Monthly"

            case "weekly":
                return "Weekly"

            case "biWeekly":
                return "Bi-Weekly"
        }
    }

    const payFrequencyTemplate = (rowData) => {
        return (
            <div>
                <Tag value={getFrequencyType(rowData.payFrequency)} severity={getFrequencyTagColor(rowData.payFrequency)}/>
            </div>
        )
    }

    const updatedAtTemplate = (rowData) => {
        return moment(rowData.updatedOn).format("YYYY-MM-DD HH:mm:ss")
    }

    const formatCurrency = (value) => {
        return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    };

    const toRateTemplate = (rowData) => {
        return formatCurrency(Number.parseFloat(rowData.toRate));
    };

    const tableHeader = () => {
        return (
            <div className="user-historical-rate-table-header">
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
                </IconField>
            </div>
        );
    }

    const columns = [
        {
            header: "Updated At",
            body: updatedAtTemplate
        },
        {
            header: "New Rate",
            body: toRateTemplate
         
        },
        {
            header: "Frequency",
            body:payFrequencyTemplate
        },
        {
            field: "updatedBy",
            header: "Update By"
        }
    ];

    const handleSelectChange = async(user) => {
       setSelectedUser(user);

        setRole(user.role);
        listUserRate(user.id);
    };

    /**
     * @async
     * @function listUserRate
     * @param {number} id - user ID
     * @description - fetches the selected user's current rate and historical rate changes
     */
    const listUserRate = async (id) => {
        try {
            setLoading(true)
            setData([])
            let response = await getUserRateRequest(id)
            if(response.id !== undefined){
                let u = {
                    id: response.id,
                    firstName: response.firstName,
                    lastname: response.lastname,
                    rate: response.rate,
                    payFrequency: response.payFrequency
                }
                setRateInput(u.rate)
                setCurrentRate(u.rate)
                let frequency = payFrequencies.find(x => x.value === u.payFrequency)

                setSelectedFrequency(frequency.value)
    
            }
            let userHistoricalRate = await getUserHistoricalRateRequest(id)
            setData(userHistoricalRate)
        } catch (error) {
            console.error(error)
        } finally{
            setLoading(false)
        }
    
    }

    /**
     * @async
     * @function updateUserRate
     * @description - updates user's payment rate
     */
    const updateUserRate = async() => {
        try {
            const userId = selectedUser.id

            const keys = {
                fromRate: Number.parseFloat(currentRate).toString(), 
                toRate: Number.parseFloat(rateInput).toString(),
                payFrequency: selectedFrequency
    
            }
            await createUserRateRequest(userId,keys)
            listUserRate(selectedUser.id)
            
        } catch (error) {
            console.error(error)
        }
    }

    /**
     * @async
     * @function updateUserPayFrequency
     * @description - updates user's pay frequency
     */
    const updateUserPayFrequency = async() => {
        try {
            let key = {
                payFrequency: selectedFrequency
            }
            await updateUserPayFrequencyRequest(selectedUser.id, key)
            listUserRate(selectedUser.id)
            
        } catch (error) {
            console.error(error)
        }
    }

    /**
     * @async
     * @function updateUserRole
     * @description - updates user's role
     */
    const updateUserRole = async() => {
        try {
            await updateUserSystemRole(selectedUser.id, { role })
            getUsers()
        } catch (error) {
            console.error(error)
        }
    }
    
    /**
     * @async
     * @function getRoles
     * @description - fetches available user roles
     */
    async function getRoles(){
        try {
            let list = []
            let roles = await getAllSystemRolesRequest()
            roles.forEach(e => {
                let model = {
                    value: '',
                    label: ''
                }
                model.value = e.id
                model.label = `${e.name}`
                list.push(model)
            });
            setRoles(list)
            
        } catch (error) {
            console.error(error)
        }
    }

    const getUsers = async(event) => {
        try {
            setLoadingUsers(true)
            const response = await getUserRateRequest(0, event.query)
            let userList = []
            
            for (const user of response) {
                userList.push({
                    id: user.id,
                    name: user.firstName + " " + user.lastName,
                    role: user.systemRole
                })
            }
            setUsers(userList)
        } catch (error) {
            console.error(error)
        } finally {
            setLoadingUsers(false)

        }
    }

    useEffect(() => {
        getRoles();
      },[]);

    return (
        <div className="settingsContainer">
            <AdminUserSettingsHeaderLink/>
            {  
                <Box sx={{ flexGrow: 1 }} className="settingsBody">
                    <Grid container spacing={2}>
                        <Grid size={10}>
                            <h3 className="settingsLabel">Update Rate and Role</h3>

                        </Grid>
                        <Grid size={10}>
                            <AutoComplete 
                                value={searchString}
                                onChange={(e) => setSearchString(e.value)}
                                onSelect={(e) => handleSelectChange(e.value)}
                                suggestions={users}
                                completeMethod={getUsers}
                                field="name"
                                placeholder="Search for a user..."
                            />
                            {selectedUser && (
                                <div className="user-rate">
                                    <div className="input-button-group">
                                        <WisdomDropdown 
                                            value={selectedFrequency}
                                            onChange={(e) => setSelectedFrequency(e.value)}
                                            options={payFrequencies}
                                            optionalLabel="label"
                                            optionalValue="value"
                                            placeholder='Select an pay frequency'
                                            checkmark={true}
                                        />
                                        <Can permission={PERMISSIONS.ratesUsersByIdPayFrequency_patch}>
                                            <Button label="Save" severity="success" onClick={updateUserPayFrequency} className="save-btn" />
                                        </Can>
                                    </div>

                                    <div className="input-button-group">
                                        <input
                                            className="rate-input"
                                            type="number"
                                            value={Number.parseFloat(rateInput)}
                                            onChange={e => setRateInput(e.target.value)}
                                        />
                                        <Can permission={PERMISSIONS.ratesById_post}>
                                            <Button label="Save" severity="success" onClick={updateUserRate} className="save-btn" />
                                        </Can>
                                    </div>

                                    <div className="input-button-group">
                                        <WisdomDropdown
                                            placeholder="Select a role"
                                            value={role}
                                            highlightOnSelect={false}
                                            checkmark={true}
                                            onChange={(e) => setRole(e.value)}
                                            optionalLabel='label'
                                            optionalValue='value'
                                            options={roles}
                                        />
                                        <Can permission={PERMISSIONS.usersByIdRole_patch}>
                                            <Button label="Save" severity="success" onClick={updateUserRole} className="save-btn" />
                                        </Can>
                                    </div>

                                </div>
                            )}
                        </Grid>
                        <div className="rate-table">
                            {loading === true
                                ? <LoadingSpinner />
                                : <WisdomDataTable header={tableHeader} data={data} columns={columns} filters={filters} globalFilters={globalFilters} rows={10}/>
                            }
                        </div>
                    </Grid>
                </Box>
            }
        </div>
    );
}

export default UpdateUser;